// Выделение нескольких задач: Ctrl+клик, рамка по пустому месту сетки, групповые действия.
// Живёт в состоянии экрана, не хранится и не синхронизируется.
import { confirmDialog } from '@core/ui/confirm';
import { isPinned } from './pins';
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { data, useData } from '@core/store';
import type { MenuItem } from '@core/ui/Menu';
import { Button } from '@core/ui/Button';
import { applyPlanned } from './apply';
import { assigneesNow, freshOf, rowTasksOf, taskNow, tasksNow, type Task } from './data';
import { planBacklog, planDuplicateMany, planGroupMove } from './planner';
import { daysBetween } from '@core/dates';
import { PRESS_THRESHOLD, deleteQuestion, refocus, useEdgeScroll, useEscape } from './interaction';
import { Archive, ArchiveRestore, Copy, Eraser, Highlighter, MessageCircleQuestion, Pin, PinOff, SquarePen, Trash2, X } from 'lucide-react';

export type Selection<T extends { id: string } = Task> = {
  ids: ReadonlySet<string>;
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  replace: (ids: Iterable<string>) => void;
  clear: () => void;
  /** Группа для действия над задачей: всё выделение, если задача в нём и оно больше одной; иначе null. */
  groupOf: (task: { id: string }) => T[] | null;
};

/**
 * visible — задачи, которые сейчас на сетке: выделение чистится от исчезнувших (удалены, в бэклоге, вне диапазона).
 * resolve — строки по id из подтверждённого состояния (роадмап — task, лонч — launch_task).
 */
export function useSelection<T extends { id: string }>(visible: ReadonlySet<string>, resolve: (ids: Iterable<string>) => T[]): Selection<T> {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    setIds((prev) => {
      if (![...prev].some((id) => !visible.has(id))) return prev;
      return new Set([...prev].filter((id) => visible.has(id)));
    });
  }, [visible]);
  // другим — рамка цвета человека на выделенных (см. useAttention); уход с экрана снимает
  useEffect(() => { data.updatePresence({ selected: [...ids] }); }, [ids]);
  useEffect(() => () => data.updatePresence({ selected: [] }), []);
  useEscape(ids.size > 0, () => setIds(new Set()));
  // ушёл на 15 минут — выделение снято, чтобы не висеть у других после возвращения
  const idle = useData((s) => s.idle);
  useEffect(() => { if (idle) setIds(new Set()); }, [idle]);
  return useMemo(() => ({
    ids,
    has: (id) => ids.has(id),
    toggle: (id) => setIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }),
    replace: (next) => setIds(new Set(next)),
    clear: () => setIds(new Set()),
    groupOf: (task) => (ids.has(task.id) && ids.size > 1 ? resolve(ids) : null),
  }), [ids, resolve]);
}

export const clearSelectionItem = (s: Pick<Selection<{ id: string }>, 'clear'>): MenuItem => ({ label: 'Clear selection', shortcut: 'Esc', onSelect: s.clear, icon: X });

// ---------------------------------------------------------------- рамка

type Rect = { left: number; top: number; right: number; bottom: number };
const intersects = (a: Rect, b: Rect) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

/** Левое нажатие на пустом месте тела сетки и протяжка: задачи под рамкой попадают в выделение. selector — атрибут бара с id. */
export function useMarquee(gridRef: RefObject<HTMLDivElement | null>, selection: Pick<Selection<{ id: string }>, 'ids' | 'replace' | 'clear'>, selector: 'data-task' | 'data-launch-task' = 'data-task') {
  const [rect, setRect] = useState<Rect | null>(null);
  const edge = useEdgeScroll(gridRef);
  const latest = useRef(selection);
  latest.current = selection;

  // слушаем окно и проверяем цель: тело сетки монтируется после «Loading…», позже первого эффекта
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const g = gridRef.current;
      if (e.button !== 0 || !g || !(e.target instanceof Node) || !g.contains(e.target)) return;
      const t = e.target as HTMLElement;
      if (t.closest(`[${selector}], [role="button"], button, a, input`)) return;
      // preventDefault не даёт тянуться текстовому выделению, но и не снимает фокус с бара — снимаем сами
      e.preventDefault();
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      const additive = e.ctrlKey || e.metaKey;
      const base = additive ? [...latest.current.ids] : [];
      const origin = { x: e.clientX, y: e.clientY };
      let last = origin;
      let moved = false;
      const bounds = () => g.getBoundingClientRect();
      const frame = (x: number, y: number): Rect => {
        const b = bounds();
        const cx = Math.max(b.left, Math.min(b.right, x));
        const cy = Math.max(b.top, Math.min(b.bottom, y));
        return { left: Math.min(origin.x, cx), top: Math.min(origin.y, cy), right: Math.max(origin.x, cx), bottom: Math.max(origin.y, cy) };
      };
      const hits = (r: Rect) => {
        const out = new Set<string>(base);
        for (const el of g.querySelectorAll<HTMLElement>(`[${selector}]`)) {
          if (intersects(r, el.getBoundingClientRect())) out.add(el.getAttribute(selector)!);
        }
        return out;
      };
      const update = (x: number, y: number) => {
        last = { x, y };
        if (!moved && Math.abs(x - origin.x) + Math.abs(y - origin.y) <= PRESS_THRESHOLD) return;
        moved = true;
        const r = frame(x, y);
        setRect(r);
        latest.current.replace(hits(r));
      };
      const onMove = (ev: PointerEvent) => {
        update(ev.clientX, ev.clientY);
        // у края тела — прокрутка; рамка стоит на месте, а задачи под ней меняются
        edge.track(ev.clientX, ev.clientY, () => update(last.x, last.y));
      };
      const onUp = () => {
        edge.stop();
        window.removeEventListener('pointermove', onMove);
        setRect(null);
        if (!moved) { latest.current.clear(); return; }
        // клавиши (Delete, стрелки, Ctrl+D) слушает бар в фокусе — после рамки это верхний левый из попавших
        const r = frame(last.x, last.y);
        const first = [...g.querySelectorAll<HTMLElement>(`[${selector}]`)].filter((el) => intersects(r, el.getBoundingClientRect()))
          .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top || a.getBoundingClientRect().left - b.getBoundingClientRect().left)[0];
        first?.focus({ preventScroll: true });
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp, { once: true });
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [gridRef, edge, selector]);

  const node = rect ? (
    <div
      className="fixed border-2 border-accent bg-accent/10 pointer-events-none z-[7]" aria-hidden
      style={{ left: rect.left, top: rect.top, width: rect.right - rect.left, height: rect.bottom - rect.top }}
    />
  ) : null;
  return { marquee: node };
}

// ---------------------------------------------------------------- групповые действия

type Single = {
  duplicate: (t: Task) => void | Promise<void>;
  backlog: (t: Task, inBacklog: boolean) => void | Promise<void>;
  remove: (t: Task) => void | Promise<void>;
};

/** Действия, которые у выделенной задачи касаются всего выделения; у одиночной — как раньше. */
export function useGroupActions(selection: Selection, fail: (err?: string) => void, single: Single) {
  const duplicate = useCallback(async (t: Task) => {
    const group = selection.groupOf(t);
    if (!group) return single.duplicate(t);
    const newIds = group.map(() => crypto.randomUUID());
    const r = await applyPlanned(() => {
      const fresh = tasksNow(group.map((g) => g.id));
      return { changes: planDuplicateMany(fresh, assigneesNow(), rowTasksOf, newIds), op: { kind: 'duplicate_tasks', task_ids: fresh.map((x) => x.id), new_task_ids: newIds } };
    });
    if (!r.ok) fail(r.error);
    else { selection.replace(newIds); refocus(`[data-task="${newIds[0]}"]`); }
  }, [selection, fail, single]);

  const backlog = useCallback(async (t: Task, inBacklog: boolean) => {
    const group = selection.groupOf(t);
    if (!group) return single.backlog(t, inBacklog);
    const r = await applyPlanned(() => ({
      changes: group.flatMap((g) => planBacklog(freshOf(g), inBacklog)),
      op: { kind: inBacklog ? 'to_backlog_many' : 'from_backlog_many', task_ids: group.map((g) => g.id) },
    }));
    if (!r.ok) fail(r.error);
  }, [selection, fail, single]);

  const remove = useCallback(async (t: Task) => {
    const group = selection.groupOf(t);
    if (!group) return single.remove(t);
    if (!(await confirmDialog({ title: 'Delete tasks', text: deleteQuestion(group.length), yes: 'Delete', danger: true }))) return;
    const r = await data.applyChanges(group.map((g) => ({ table: 'task' as const, id: g.id, delete: true as const })), { kind: 'delete_tasks', task_ids: group.map((g) => g.id) });
    if (!r.ok) fail(r.error);
  }, [selection, fail, single]);

  /** Меню выделенной задачи вместо одиночного. */
  const menuFor = useCallback((t: Task): MenuItem[] | null => {
    const group = selection.groupOf(t);
    if (!group) return null;
    const n = group.length;
    return [
      { label: `Duplicate ${n} tasks`, shortcut: 'Ctrl+D', onSelect: () => void duplicate(t), icon: Copy },
      { label: `${n} tasks to the backlog`, onSelect: () => void backlog(t, true), icon: Archive },
      'sep',
      { label: `Delete ${n} tasks`, shortcut: 'Delete', onSelect: () => void remove(t), danger: true, icon: Trash2 },
      'sep',
      clearSelectionItem(selection),
    ];
  }, [selection, duplicate, backlog, remove]);

  return { duplicate, backlog, remove, menuFor };
}

/** Групповой сдвиг по времени после броска: все задачи группы на столько дней, на сколько уехала взятая. */
export async function applyGroupMove(grabbed: Task, groupIds: string[], targetStart: string, fail: (err?: string) => void): Promise<void> {
  const r = await applyPlanned(() => {
    const t = freshOf(grabbed);
    return { changes: planGroupMove(tasksNow(groupIds), daysBetween(t.start_date, targetStart), rowTasksOf), op: { kind: 'move_tasks', task_ids: groupIds } };
  });
  if (!r.ok) fail(r.error);
}

/** Сдвиг членов группы во время перетаскивания: id и дни, пока взятый бар едет; null — не группа. */
export function groupShiftOf<T extends { id: string; start_date: string }>(d: { kind: string; group: string[]; task: T; preview: { kind: string; start_date: string } | null } | null, fresh: (id: string) => T | undefined = taskNow as (id: string) => T | undefined): { ids: Set<string>; days: number } | null {
  if (!d || d.kind !== 'move' || d.group.length < 2 || !d.preview || d.preview.kind !== 'move') return null;
  return { ids: new Set(d.group), days: daysBetween((fresh(d.task.id) ?? d.task).start_date, d.preview.start_date) };
}

type MenuActions = {
  openTask: (t: Task, tab?: 'task' | 'questions') => void;
  pin: (t: Task) => void | Promise<void>;
  highlight: { is: (kind: 'task', id: string) => boolean; toggle: (s: { kind: 'task'; id: string }) => void };
  group: { duplicate: (t: Task) => void | Promise<void>; backlog: (t: Task, inBacklog: boolean) => void | Promise<void>; remove: (t: Task) => void | Promise<void>; menuFor: (t: Task) => MenuItem[] | null };
  fromBacklog?: boolean; // Projects: бар в бэклоге может вернуться в план
};

/** Меню бара: те же действия, что хоткеи и карточка; у выделенной задачи — над всем выделением. */
export function taskMenu(t: Task, a: MenuActions & { canWrite?: boolean }): MenuItem[] {
  // наблюдатель смотрит и спрашивает: подсветка личная, остальное меняет план
  if (a.canWrite === false) {
    return [
      { label: 'Open', shortcut: 'Enter', onSelect: () => a.openTask(t), icon: SquarePen },
      { label: 'Ask a question', onSelect: () => a.openTask(t, 'questions'), icon: MessageCircleQuestion },
    ];
  }
  return a.group.menuFor(t) ?? [
    { label: 'Open', shortcut: 'Enter', onSelect: () => a.openTask(t), icon: SquarePen },
    { label: 'Ask a question', onSelect: () => a.openTask(t, 'questions'), icon: MessageCircleQuestion },
    'sep',
    { label: 'Duplicate', shortcut: 'Ctrl+D', onSelect: () => void a.group.duplicate(t), icon: Copy },
    // задаче в бэклоге пин и подсветка не нужны: её нет на сетке
    ...(t.in_backlog ? [] : [
      { label: isPinned(t.id) ? 'Unpin from the header' : 'Pin to the header', shortcut: 'Ctrl+P', onSelect: () => void a.pin(t), icon: isPinned(t.id) ? PinOff : Pin },
      { label: a.highlight.is('task', t.id) ? 'Remove the highlight' : 'Highlight', onSelect: () => a.highlight.toggle({ kind: 'task', id: t.id }), icon: a.highlight.is('task', t.id) ? Eraser : Highlighter },
    ]),
    { label: a.fromBacklog && t.in_backlog ? 'Back to the plan' : 'To the backlog', onSelect: () => void a.group.backlog(t, !(a.fromBacklog && t.in_backlog)), icon: a.fromBacklog && t.in_backlog ? ArchiveRestore : Archive },
    'sep',
    { label: 'Delete', shortcut: 'Delete', onSelect: () => void a.group.remove(t), danger: true, icon: Trash2 },
  ];
}

/** Счётчик в тулбаре: сколько выделено и сброс. */
export function SelectionChip({ selection }: { selection: Pick<Selection<{ id: string }>, 'ids' | 'clear'> }) {
  if (selection.ids.size < 2) return null; // одну выделенную видно по кольцу; чип — про группу
  return (
    <span className="flex items-center gap-2 text-sm" data-selection-count={selection.ids.size}>
      <span className="font-medium">{selection.ids.size} selected</span>
      <Button variant="ghost" size="sm" className="px-2" onClick={selection.clear} title="Clear the selection (Esc)">Clear</Button>
    </span>
  );
}
